import React from 'react';
import { observer } from 'mobx-react';
import { UserEntity } from 'Models/Entities';
import { appConfigStore } from 'Models/AppConfigStore';
import { TextInput } from 'Views/Components/New/TextInput';
import { PhoneInputField } from 'Views/Components/New/PhoneInputField';
import { Checkbox } from 'Views/Components/Checkbox/Checkbox';

export interface RegisterCustomerFieldsProps {
	userEntity: UserEntity;
	errors: Record<string, string>;
	onAfterChange: () => void;
	autoFocus?: boolean;
	/** Whether the customer is being registered as a company. */
	registerAsCompany?: boolean;
	/** Toggles the company option. Omit to hide the option entirely. */
	onRegisterAsCompanyChange?: (checked: boolean) => void;
}

type RegisterCustomerField = 'email' | 'firstName' | 'lastName' | 'phone' | 'postcode' | 'companyName';

/**
 * The customer detail inputs for registering a new customer (email, name, and the config-gated
 * phone/postcode fields). Shared between the register-customer page and the inline register step
 * in the purchase modals so the field set stays centralised.
 */
function RegisterCustomerFieldsContent({
	userEntity,
	errors,
	onAfterChange,
	autoFocus = true,
	registerAsCompany = false,
	onRegisterAsCompanyChange,
}: RegisterCustomerFieldsProps) {
	const { userPhoneEnabled, userPhoneRequired, userPostcodeEnabled, userCompanyNameEnabled } = appConfigStore;

	const showCompanyOption = userCompanyNameEnabled && onRegisterAsCompanyChange !== undefined;
	const isCompany = showCompanyOption && registerAsCompany;

	const bind = (field: RegisterCustomerField) => ({
		value: userEntity[field] ?? '',
		onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
			userEntity[field] = e.target.value;
			onAfterChange();
		},
		error: errors[field],
	});

	return (
		<div className="register-customer-fields flex flex-col gap-3" data-test-id="register-customer-fields">
			<TextInput
				label="Email"
				placeholder="Email"
				required
				autoFocus={autoFocus}
				data-test-id="register-customer-email"
				{...bind('email')}
			/>
			{showCompanyOption && (
				<Checkbox
					model={{ registerAsCompany }}
					modelProperty="registerAsCompany"
					label="Register as a company"
					inputProps={{ 'data-test-id': 'register-customer-as-company' } as any}
					onChecked={(_, checked) => onRegisterAsCompanyChange?.(checked)}
				/>
			)}
			{isCompany && (
				<TextInput
					label="Company name"
					placeholder="Company name"
					required
					data-test-id="register-customer-company-name"
					{...bind('companyName')}
				/>
			)}
			<div className="flex gap-3">
				<TextInput
					label={isCompany ? 'Contact first name' : 'First name'}
					placeholder={isCompany ? 'Contact first name' : 'First name'}
					required
					data-test-id="register-customer-first-name"
					{...bind('firstName')}
				/>
				<TextInput
					label={isCompany ? 'Contact last name' : 'Last name'}
					placeholder={isCompany ? 'Contact last name' : 'Last name'}
					required
					data-test-id="register-customer-last-name"
					{...bind('lastName')}
				/>
			</div>
			{userPhoneEnabled && (
				<PhoneInputField
					label="Phone"
					placeholder="Phone"
					required={userPhoneRequired}
					data-test-id="register-customer-phone"
					model={userEntity}
					modelProperty="phone"
					error={errors.phone}
					onAfterChange={onAfterChange}
				/>
			)}
			{userPostcodeEnabled && (
				<TextInput
					label="Postcode"
					placeholder="Postcode"
					data-test-id="register-customer-postcode"
					// always required since postcode is enabled and is customer
					required={userPostcodeEnabled}
					{...bind('postcode')}
				/>
			)}
		</div>
	);
}

// Observer is required so the fields re-render when the model values / validation errors change.
export const RegisterCustomerFields = observer(RegisterCustomerFieldsContent);
